/** Basic aggregate helpers for arrays and grouped datasets. */
export namespace Aggregates {
  /** Count items in a collection. */
  export function count<T>(values: readonly T[]): number {
    return values.length;
  }

  /** Sum numeric values. */
  export function sum(values: readonly number[]): number {
    let total = 0;
    for (const value of values) {
      total += value;
    }
    return total;
  }

  /** Smallest numeric value, if any. */
  export function min(values: readonly number[]): Option<number> {
    if (values.length === 0) return null;

    let result = values[0];
    for (let i = 1; i < values.length; i++) {
      if (values[i] < result) result = values[i];
    }
    return result;
  }

  /** Largest numeric value, if any. */
  export function max(values: readonly number[]): Option<number> {
    if (values.length === 0) return null;

    let result = values[0];
    for (let i = 1; i < values.length; i++) {
      if (values[i] > result) result = values[i];
    }
    return result;
  }

  /** Arithmetic mean, if any. */
  export function mean(values: readonly number[]): Option<number> {
    if (values.length === 0) return null;
    return sum(values) / values.length;
  }

  /** Median value, if any. */
  export function median(values: readonly number[]): Option<number> {
    if (values.length === 0) return null;

    const sorted = [...values];
    sorted.sort((a, b) => a - b);

    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) return sorted[mid];
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  /** First value, if any. */
  export function first<T>(values: readonly T[]): Option<T> {
    if (values.length === 0) return null;
    return values[0];
  }

  /** Last value, if any. */
  export function last<T>(values: readonly T[]): Option<T> {
    if (values.length === 0) return null;
    return values[values.length - 1];
  }

  /** Min/max extent, if any. */
  export function extent(
    values: readonly number[],
  ): Option<{ min: number; max: number }> {
    const minValue = min(values);
    if (minValue == null) return null;

    const maxValue = max(values);
    if (maxValue == null) return null;

    return { min: minValue, max: maxValue };
  }
}
